import React, { useState, useEffect } from 'react';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import supabase from '../supabaseClient';

const AllergenPreferencesStep = ({ onSave, initialValues = [], sessionToken }) => {
    const [ingredients, setIngredients] = useState([]);
    const [selectedAllergens, setSelectedAllergens] = useState(initialValues);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [commonAllergens, setCommonAllergens] = useState([]);
    
    // Common allergen ingredient IDs
    const commonAllergenIds = [
        'ac68b8ae-7a19-47f8-9a68-8066d053a5d8',
        '19b47f77-ab05-4522-98a4-0843a6a053d4',
        '24225104-ee2a-4621-992a-39a54ddbc974',
        '624ea733-4c8b-418b-b135-0fcaf48eaf43',
        '6d48618a-427f-4904-bf59-48a3b7584cef',
        'a73a7306-2517-44e8-a5e4-cdcfc7b651ec',
        'd436d4ff-ab9c-4ad5-ba3f-70288f02c50f',
        '0bf5d427-b680-4678-a5cb-94b9c5622562',
        'b8c9d504-2907-41bc-b21c-1fefed5e0cb2'
    ];

    useEffect(() => {
        const fetchIngredients = async () => {
            try {
                if (!sessionToken) {
                    console.error('No session token available');
                    return;
                }

                const response = await fetchWithCSRF('/api/v1/ingredient', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                if (!Array.isArray(data)) {
                    throw new Error('Expected array of ingredients from API');
                }
                setIngredients(data);
            } catch (error) {
                console.error('Error fetching ingredients:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchIngredients();
    }, [sessionToken]);

    useEffect(() => {
        const fetchCommonAllergens = async () => {
            try {
                if (!sessionToken) {
                    console.error('No session token available');
                    return;
                }

                const response = await fetchWithCSRF('/api/v1/ingredient/multiple_ids/', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ ids: commonAllergenIds })
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                if (!Array.isArray(data)) {
                    throw new Error('Expected array of ingredients from API');
                }
                setCommonAllergens(data);
                
                // Preload images for common allergens
                data.forEach(allergen => {
                    const img = new Image();
                    img.src = getIngredientImageUrl(allergen.id);
                });
            } catch (error) {
                console.error('Error fetching common allergens:', error);
            }
        };

        fetchCommonAllergens();
    }, [sessionToken]);

    useEffect(() => {
        // Filter ingredients based on search query
        if (searchQuery.trim() === '') {
            setSearchResults([]);
        } else {
            const filtered = ingredients.filter(ingredient => 
                ingredient.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
        }
    }, [searchQuery, ingredients]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSelectAllergen = (ingredient) => {
        // Add the selected allergen if it's not already selected
        if (!selectedAllergens.includes(ingredient.id)) {
            setSelectedAllergens(prev => [...prev, ingredient.id]);
        }
        // Don't reset the search query
        // setSearchQuery(''); // Remove or comment out this line
    };

    const handleRemoveAllergen = (allergenId) => {
        setSelectedAllergens(prev => prev.filter(id => id !== allergenId));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(selectedAllergens);
    };

    // Get ingredient names for display
    const getIngredientName = (id) => {
        const ingredient = ingredients.find(ing => ing.id === id);
        return ingredient ? ingredient.name : `Ingredient ${id}`;
    };

    if (loading) {
        return <div className="text-center">Loading...</div>;
    }

    return (
        <div className="preference-step">
            <h2>Any ingredients you want to avoid?</h2>
            <p>Search for ingredients you don't like or prefer not to eat. We'll make sure they're not included in your meals.</p>
            
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <div className="search-container mb-3">
                        <input
                            type="text"
                            className="form-control form-control-custom"
                            placeholder="Search for ingredients..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>
                    
                    {/* Show search results when typing, otherwise show common allergens */}
                    {searchResults.length > 0 ? (
                        <div className="mb-4">
                            <div className="search-results-buttons d-flex flex-wrap gap-2">
                                {searchResults.map(ingredient => (
                                    <button
                                        key={ingredient.id}
                                        type="button"
                                        className="btn btn-outline- allergen-ingredient-option"
                                        onClick={() => handleSelectAllergen(ingredient)}
                                    >
                                        {ingredient.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        /* Common Allergens Section - only show when not searching */
                        searchQuery.trim() === '' && commonAllergens.filter(allergen => !selectedAllergens.includes(allergen.id)).length > 0 && (
                            <div className="mb-4">
                                <h6 className="text-muted mb-3" style={{ textAlign: 'left' }}>Common allergens:</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {commonAllergens
                                        .filter(allergen => !selectedAllergens.includes(allergen.id))
                                        .map(allergen => (
                                            <button
                                                key={allergen.id}
                                                type="button"
                                                className="btn btn-outline- allergen-ingredient-option d-flex align-items-center gap-2"
                                                onClick={() => handleSelectAllergen(allergen)}
                                            >
                                                <img 
                                                    src={getIngredientImageUrl(allergen.id)} 
                                                    alt={allergen.name}
                                                    style={{ width: '25px', height: '25px', objectFit: 'cover', borderRadius: '4px' }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                <span>{allergen.name}</span>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )
                    )}
                    
                    <div className="selected-allergens mt-5 mb-5">
                        {selectedAllergens.length > 0 && <h6 className="text-muted">You won't see these ingredients in your meals</h6>}
                        {selectedAllergens.length === 0 ? (
                            <p className="text-muted"></p>
                        ) : (
                            <div className="d-flex flex-wrap gap-2">
                                {selectedAllergens.map(allergenId => (
                                    <div key={allergenId} className="badge d-flex align-items-center p-2 selected-allergen-ingredient-badge">
                                        <img 
                                            src={getIngredientImageUrl(allergenId)}
                                            alt={`${allergenId} icon`}
                                            className=""
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                            style={{ width: '25px', height: '25px', marginRight: '5px' }}
                                        />
                                        <span>{getIngredientName(allergenId)}</span>
                                        <button 
                                            type="button" 
                                            className="btn-close ms-2" 
                                            style={{ fontSize: '0.5rem' }}
                                            onClick={() => handleRemoveAllergen(allergenId)}
                                            aria-label="Remove"
                                        ></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="d-flex justify-content-between">
                    <button type="button" className="btn btn-secondary" onClick={() => onSave(null, true)}>Back</button>
                    <button type="submit" className="btn btn-primary">
                        {selectedAllergens.length === 0 ? 'Skip' : 'Continue'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AllergenPreferencesStep; 